<template>
    <div v-if="city" class="wild-next-root app-screen-bg">
        <div class="wild-next-banner" :style="bannerStyle" ref="banner">
            <span class="wild-next-title-pill">{{ $t('journey.wild.title') }}</span>
            <div
                class="wild-next-back-badge"
                :class="{'wild-next-back-badge--disabled': steps < backToCityStepCost || isLoading}"
                @click="!isLoading && backToCity()"
            >
                <div class="wild-next-back-badge-img" :style="cityImgStyle">
                    <div class="wild-next-back-badge-corner-icon">
                        <span>{{ backToCityStepCost }}</span><v-icon color="white" size="28">mdi-shoe-print</v-icon>
                    </div>
                </div>
                <div class="wild-next-back-badge-label">{{ $t('journey.wild.backToCityLabel') }}</div>
            </div>
        </div>
        <div class="wild-next-content">
            <v-row class="ma-0 pa-2 wild-next-grid" dense>
                <v-col v-for="tile in tiles" :key="tile.key" :cols="colSpan" class="text-center wild-next-col">
                    <NextCircleTile
                        :img="tile.img"
                        :overlay-img="tile.overlayImg || ''"
                        :title="tile.title"
                        :steps="tile.steps || null"
                        :corner-icon="tile.cornerIcon || ''"
                        :corner-icon-color="tile.cornerIconColor || 'white'"
                        :disabled="!!tile.disabled"
                        @click="tile.action && tile.action()"
                    />
                </v-col>
            </v-row>
        </div>

        <WildTrainerFightDialog
            v-model="showTrainerDialog"
            :trainer="wildTrainer"
            :position-id="positionId"
            :city="city"
        />
        <DungeonDetailDialog
            v-if="wild.dungeon"
            v-model="showDungeonDialog"
            :dungeon="wild.dungeon"
            :is-challenge="dungeonDialogIsChallenge"
            :position-id="positionId"
            :available-steps="steps"
        />
        <ConfirmDialog ref="confirmDialog"/>
    </div>
</template>

<script>
    // Fork Wild obrazovky - stejná kostra jako CityNext.vue (banner s titulní
    // pilulkou + grid NextCircleTile dlaždic pod ním). Design/historie iterací
    // (back-to-city badge, trainer/dungeon popupy, overlayImg miniatury) viz
    // claude/components/journey-dungeon.md.
    import {mapActions, mapGetters} from 'vuex';
    import * as journeyApi from '@/api/journeyApi';
    import {pokemonImageSrc} from '@/utils/pokemonImage';
    import NextCircleTile from './pieces/NextCircleTile';
    import WildTrainerFightDialog from './WildTrainerFightDialog';
    import DungeonDetailDialog from './DungeonDetailDialog';
    import ConfirmDialog from '@/components/utils/ConfirmDialog';
    import {computeGridCols} from '@/utils/gridLayout';

    // musí odpovídat padding-bottom v .app-screen-bg (_card.sass) - jen collapsed
    // stav TeamBottomSheet rezervuje místo v gridCols() výpočtu
    const TEAM_SHEET_HANDLE_HEIGHT = 22;
    const BACK_TO_CITY_STEP_COST = 2;
    // Jediný zdroj pravdy pro všechny možné KATEGORIE dlaždic + kolik jich max
    // může být najednou (MAX_WILD_TILES se sčítá samo, viz reduce níže). Na
    // rozdíl od City se ale prázdné placeholdery nedělají - trainer/dungeon/
    // dungeon-challenge prostě naskakují za sebou, mezera nevzniká; gridCols()
    // se přesto počítá z tohohle fixního maxima, ne z aktuálního počtu, aby se
    // dlaždice nezvětšovaly/nezmenšovaly podle toho, co zrovna padlo.
    const TILE_SLOT_DEFINITIONS = [
        {key: 'trainer', max: 1},
        {key: 'dungeon', max: 1},
        {key: 'dungeon-challenge', max: 1},
        // zrcadlí TravelAction::generateNewWild() na backendu (dnes max 4)
        {key: 'environment', max: 4},
    ];
    const MAX_WILD_TILES = TILE_SLOT_DEFINITIONS.reduce((sum, slot) => sum + slot.max, 0);

    export default {
        name: 'WildNext',
        components: {NextCircleTile, WildTrainerFightDialog, DungeonDetailDialog, ConfirmDialog},
        props: {
            wild: {type: Object, default: null},
            city: {type: Object, default: null},
            positionId: {type: Number, default: null},
            trainerId: {type: Number, default: null},
        },
        data() {
            return {
                wildTrainer: null,
                showTrainerDialog: false,
                showDungeonDialog: false,
                dungeonDialogIsChallenge: false,
                // změřeno v mounted()/measure() - dynamický výpočet gridCols() níže
                containerWidth: 0,
                containerHeight: 0,
                bannerHeight: 0,
            }
        },
        computed: {
            ...mapGetters({
                isLoading: 'journey/isLoading',
                user: 'user/item',
                steps: 'journey/steps',
            }),
            bannerStyle() {
                return {backgroundImage: `url(${require('@/assets/pic/roads/' + this.city.id + '.png')})`};
            },
            cityImgStyle() {
                return {backgroundImage: `url(${require('@/assets/pic/city/' + this.city.id + '.png')})`};
            },
            backToCityStepCost() {
                return BACK_TO_CITY_STEP_COST;
            },
            // pořadí: trenér, dungeon, challenge, pak divočiny - žádný placeholder
            // pro chybějící slot (na rozdíl od CityNext.vue tu nejsou {empty:true} sloty)
            tiles() {
                const list = [];
                if (this.wild.wildTrainerId && this.wildTrainer) {
                    list.push({
                        key: 'trainer',
                        img: require('@/assets/pic/roads/' + this.city.id + '.png'),
                        overlayImg: require('@/assets/pic/trainer/' + this.wildTrainer.img),
                        title: this.wildTrainer.name,
                        steps: 1,
                        // bez kroku by klik otevřel popup s volbou 1v1/2v2/3v3, přestože
                        // backend takový request stejně odmítne (PositionService::stepMoveDecrementer)
                        disabled: this.steps < 1,
                        action: () => { this.showTrainerDialog = true; },
                    });
                }
                if (this.wild.dungeon) {
                    list.push({
                        key: 'dungeon',
                        img: require('@/assets/pic/wild/thumb/DUNGEON.png'),
                        overlayImg: this.dungeonBossImg,
                        title: this.$t('journey.wild.dungeonTileTitle'),
                        cornerIcon: 'mdi-castle',
                        action: () => { this.dungeonDialogIsChallenge = false; this.showDungeonDialog = true; },
                    });
                    if (this.wild.dungeon.isDungeonChallenge) {
                        list.push({
                            key: 'dungeon-challenge',
                            img: require('@/assets/pic/wild/thumb/DUNGEON.png'),
                            overlayImg: this.dungeonBossImg,
                            title: this.$t('journey.wild.dungeonChallengeTileTitle'),
                            cornerIcon: 'mdi-crown',
                            cornerIconColor: 'amber',
                            action: () => { this.dungeonDialogIsChallenge = true; this.showDungeonDialog = true; },
                        });
                    }
                }
                (this.wild.newWay || []).forEach((environment) => {
                    list.push({
                        key: 'wild-' + environment,
                        img: require('@/assets/pic/wild/thumb/' + environment + '.png'),
                        title: this.formatEnvironment(environment),
                        steps: 1,
                        disabled: this.steps < 1 || this.isLoading,
                        action: () => this.callMeetPokemon(environment),
                    });
                });
                return list;
            },
            dungeonBossImg() {
                const pokemonType = this.wild.dungeon && this.wild.dungeon.pokemonType;
                return pokemonType ? pokemonImageSrc(pokemonType.img) : '';
            },
            // stejný princip jako StartingNext.vue: zkouší od nejmenšího počtu
            // sloupců (2) nahoru, první, kam se všechny řádky vejdou na výšku
            // bez scrollování, vyhrává. Na rozdíl od StartingNext.vue tu není
            // extra NAME_ROW_H (jméno/kroky jsou uvnitř čtverce, viz NextCircleTile
            // překryv). `n` je záměrně MAX_WILD_TILES (fixní), ne this.tiles.length -
            // grid se nemá zvětšovat/zmenšovat podle toho, co zrovna padlo.
            gridCols() {
                return computeGridCols({
                    n: MAX_WILD_TILES,
                    containerWidth: this.containerWidth,
                    containerHeight: this.containerHeight,
                    extraHeight: this.bannerHeight + TEAM_SHEET_HANDLE_HEIGHT,
                    rowGap: 10,
                });
            },
            colSpan() {
                return 12 / this.gridCols;
            },
        },
        created() {
            if (this.wild && this.wild.id) {
                this.loadTrainer();
                this.trainerInfoMessages(this.user.id);
            }
        },
        mounted() {
            this.measure();
            window.addEventListener('resize', this.measure);
        },
        beforeDestroy() {
            window.removeEventListener('resize', this.measure);
        },
        methods: {
            ...mapActions({
                meetPokemon: 'journey/meetPokemon',
                positionPost: 'journey/positionPost',
                trainerInfoMessages: 'trainer/loadInfoMessages',
                fetchObject: 'journey/fetchObject',
            }),
            // stejný trik jako StartingNext.vue - this.$el.clientHeight je nespolehlivý,
            // protože rodičovský <v-card> v Journey.vue nemá explicitní výšku
            measure() {
                this.$nextTick(() => {
                    if (!this.$el || !this.$el.getBoundingClientRect) {
                        return;
                    }
                    const rect = this.$el.getBoundingClientRect();
                    this.containerWidth = rect.width;
                    this.containerHeight = window.innerHeight - rect.top;
                    if (this.$refs.banner) {
                        this.bannerHeight = this.$refs.banner.offsetHeight;
                    }
                });
            },
            formatEnvironment(env) {
                return env.charAt(0) + env.slice(1).toLowerCase();
            },
            callMeetPokemon(environment) {
                const obj = {positionId: this.positionId, environment: environment, actualWildType: environment};
                this.meetPokemon(obj);
            },
            loadTrainer() {
                if (this.wild.wildTrainerId) {
                    this.fetchObject(journeyApi.trainerDetail(this.wild.wildTrainerId)).then((data) => {
                        this.wildTrainer = data;
                    });
                }
            },
            async backToCity() {
                if (this.steps < this.backToCityStepCost) {
                    return;
                }
                // cena kroku jde přes `steps` opt (ConfirmDialog vykreslí černou mdi-shoe-print ikonu)
                const question = this.$t('journey.wild.confirmBackToCity', {city: this.$t('common.cityNames.' + this.city.name)});
                if (await this.$refs.confirmDialog.open(question, {steps: this.backToCityStepCost})) {
                    this.positionPost(journeyApi.travel(this.positionId, this.city.id, this.backToCityStepCost));
                }
            },
        },
    }
</script>

<style scoped>
.wild-next-root {
    height: 100%;
    overflow: auto;
    /* pozadí i rezervované místo pro TeamBottomSheet dole řídí sdílená třída
       .app-screen-bg (assets/styles/_card.sass) - musí odpovídat TEAM_SHEET_HANDLE_HEIGHT výše */
}
.wild-next-banner {
    position: relative;
    height: 150px;
    background-size: cover;
    background-position: center;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: flex-start;
    padding-top: 10px;
}
.wild-next-title-pill {
    display: inline-block;
    background: rgba(0,0,0,0.65);
    color: #fff;
    font-size: 24px;
    font-weight: 700;
    padding: 6px 24px;
    border-radius: 20px;
    text-shadow: 1px 1px 2px rgba(0,0,0,0.6);
}
/* "Back to city" - malý odznak přes banner, ne dlaždice v gridu (jiná kategorie akce než ostatní dlaždice) */
.wild-next-back-badge {
    position: absolute;
    top: 10px;
    right: 10px;
    display: flex;
    flex-direction: column;
    align-items: center;
    cursor: pointer;
    -webkit-tap-highlight-color: transparent;
}
/* stejný šedivý/neklikatelný vzor jako NextCircleTile's `disabled` */
.wild-next-back-badge--disabled {
    opacity: 0.6;
    pointer-events: none;
}
.wild-next-back-badge-img {
    width: 95px;
    height: 95px;
    /* stejné zaoblení jako ostatní dlaždice (circle-tile-avatar-wrap) */
    border-radius: 14px;
    background-size: cover;
    background-position: center;
    border: 5px solid #C0C0C0;
    box-shadow: 0 3px 8px rgba(0,0,0,0.5);
    position: relative; /* kotva pro cornerIcon */
}
/* stejná konvence jako .circle-tile-steps-corner v NextCircleTile.vue -
   číslo + ikona botičky pohromadě jako jeden celek */
.wild-next-back-badge-corner-icon {
    position: absolute;
    top: 4px;
    right: 4px;
    display: flex;
    align-items: center;
    gap: 0;
    color: #fff;
    font-weight: 700;
    font-size: 20px;
    text-shadow: 1px 1px 3px rgba(0,0,0,0.95), 0 0 4px rgba(0,0,0,0.95), 0 0 4px rgba(0,0,0,0.95);
    pointer-events: none;
}
.wild-next-back-badge-corner-icon .v-icon {
    filter: drop-shadow(1px 1px 3px rgba(0,0,0,0.95)) drop-shadow(0 0 4px rgba(0,0,0,0.95));
}
.wild-next-back-badge-label {
    margin-top: -14px;
    position: relative;
    z-index: 1;
    background: rgba(0,0,0,0.65);
    color: #fff;
    font-size: 22px;
    font-weight: 700;
    padding: 2px 8px;
    border-radius: 10px;
    text-shadow: 1px 1px 2px rgba(0,0,0,0.6);
    white-space: nowrap;
}
.wild-next-content {
    padding-top: 12px;
}
.wild-next-grid {
    row-gap: 10px;
}
.wild-next-col {
    padding: 0 4px !important;
}
</style>
